import React, { useState, useEffect, useCallback } from "react";
import { X, Plus, CheckCircle, XCircle, Clock, ArrowRight, Check, Minus } from "lucide-react";
import api from "../api";
import { K } from "../theme";
import { Bg, Bt, Cd, Sl, Inp, Tbl } from "./index";
import { notify } from "../notify";
import { fmtDMY } from "../dateFormat";
import { fmtTime } from "../utils/timezone";

// Shift change requests: raise one (for yourself or, with permission, for a team member), follow it, and
// approve / reject it when it is yours to decide. Server rules live in server/routes/shiftRequests.js.

const STATUS_COLOR = { "Pending Manager": K.a, "Pending Final": K.pu, Approved: K.g, Rejected: K.r, Cancelled: K.t3 };
const STATUS_LABEL = { "Pending Manager": "Waiting for manager", "Pending Final": "Waiting for Centre Head / HR", Approved: "Approved", Rejected: "Rejected", Cancelled: "Cancelled" };
const EVENT_LABEL = { Submitted: "Request sent for approval", "Manager Approved": "Approved by manager", Approved: "Approved", Rejected: "Rejected", Cancelled: "Cancelled by requester" };
const EVENT_COLOR = { Submitted: K.bl, "Manager Approved": K.pu, Approved: K.g, Rejected: K.r, Cancelled: K.t3 };

const hm = (t) => String(t || "").slice(0, 5);
const dateRange = (a, b) => (a === b ? fmtDMY(a) : `${fmtDMY(a)} – ${fmtDMY(b)}`);
const shiftText = (name, s, e) => (name ? `${name} (${hm(s)} – ${hm(e)})` : "Default / no shift");
const when = (ts) => (ts ? `${fmtDMY(ts)} ${fmtTime(ts)}` : "");
// One line of the approval log for the list: who decided / where it is.
const decisionText = (r) => {
  if (r.decided_action === "Approved") return `Approved by ${r.decided_by || "—"} · ${fmtDMY(r.decided_at)}${r.manager_approved_by ? ` (manager: ${r.manager_approved_by})` : ""}`;
  if (r.decided_action === "Rejected") return `Rejected by ${r.decided_by || "—"} · ${fmtDMY(r.decided_at)}`;
  if (r.decided_action === "Cancelled") return `Cancelled · ${fmtDMY(r.decided_at)}`;
  if (r.status === "Pending Final") return `Manager approved (${r.manager_approved_by || "—"}) · waiting for Centre Head / HR`;
  return `Waiting for ${r.manager_name || "manager"}`;
};
// ─── Where is this request? ────────────────────────────────────────────
// Steps: Raised → Manager (only when the person raised it for themselves and has a manager) →
// Centre Head / Centre HR / HR Head → Shift applied. Each step is done, waiting, rejected, skipped or ahead.
const buildSteps = (r) => {
  const hasMgr = r.raised_by === r.employee_id && !!r.manager_id;
  const st = r.status;
  const steps = [{ key: "raised", label: "Request raised", state: "done", who: r.raised_by_name || r.raised_by, at: r.created_at }];
  if (hasMgr) {
    let s;
    if (r.manager_approved_by) s = { state: "done", who: `Approved by ${r.manager_approved_by}`, at: r.manager_approved_at };
    else if (st === "Rejected") s = { state: "rejected", who: `Rejected by ${r.decided_by || "—"}`, at: r.decided_at };
    else if (st === "Cancelled") s = { state: "cancelled", who: "Cancelled", at: r.decided_at };
    else if (st === "Approved") s = { state: "skipped", who: `Approved directly by ${r.decided_by || "—"}`, at: r.decided_at };
    else s = { state: "waiting", who: `Waiting for ${r.manager_name || "reporting manager"}` };
    steps.push({ key: "manager", label: "Reporting manager", ...s });
  }
  let f;
  if (st === "Approved") f = { state: "done", who: `Approved by ${r.decided_by || "—"}`, at: r.decided_at };
  else if (st === "Rejected" && (!hasMgr || r.manager_approved_by)) f = { state: "rejected", who: `Rejected by ${r.decided_by || "—"}`, at: r.decided_at };
  else if (st === "Cancelled" && (!hasMgr || r.manager_approved_by)) f = { state: "cancelled", who: "Cancelled", at: r.decided_at };
  else if (st === "Pending Final") f = { state: "waiting", who: "Waiting for Centre Head / Centre HR / HR Head" };
  else f = { state: "ahead", who: "Centre Head / Centre HR / HR Head" };
  steps.push({ key: "final", label: "Final approval", ...f });
  steps.push({ key: "applied", label: "Shift applied", state: st === "Approved" ? "done" : "ahead", who: st === "Approved" ? `On ${dateRange(r.date_from, r.date_to)}` : "After final approval" });
  return steps;
};
const STEP_STYLE = {
  done:      { c: K.g,  Icon: Check },
  skipped:   { c: K.g,  Icon: Check },
  waiting:   { c: K.a,  Icon: Clock },
  rejected:  { c: K.r,  Icon: X },
  cancelled: { c: K.t3, Icon: Minus },
  ahead:     { c: K.t3, Icon: null },
};
const Stepper = ({ r, compact }) => {
  const steps = buildSteps(r);
  if (compact) {
    return (
      <div className="flex items-center gap-[3px]" title={steps.map((x) => `${x.label}: ${x.who}`).join("\n")}>
        {steps.map((x, i) => {
          const { c } = STEP_STYLE[x.state];
          return <React.Fragment key={x.key}>
            <span className="w-[9px] h-[9px] rounded-full inline-block" style={x.state === "ahead" ? { border: `1.5px solid ${c}55` } : { background: c }} />
            {i < steps.length - 1 && <span className="w-[10px] h-[2px] inline-block" style={{ background: x.state === "done" || x.state === "skipped" ? K.g : `${K.t3}44` }} />}
          </React.Fragment>;
        })}
      </div>
    );
  }
  return (
    <div className="grid gap-0" style={{ gridTemplateColumns: `repeat(${steps.length}, minmax(0, 1fr))` }}>
      {steps.map((x, i) => {
        const { c, Icon } = STEP_STYLE[x.state];
        const prevDone = i > 0 && ["done", "skipped"].includes(steps[i - 1].state);
        return (
          <div key={x.key} className="flex flex-col items-center text-center px-1">
            <div className="flex items-center w-full">
              <div className="flex-1 h-[2px]" style={{ background: i === 0 ? "transparent" : prevDone ? K.g : `${K.t3}44` }} />
              <div className={`w-[28px] h-[28px] rounded-full flex items-center justify-center flex-shrink-0 ${x.state === "waiting" ? "animate-pulse" : ""}`}
                style={x.state === "ahead" ? { border: `2px solid ${c}55`, background: "transparent" } : { background: c, color: "#fff" }}>
                {Icon && <Icon size={14} strokeWidth={3} />}
              </div>
              <div className="flex-1 h-[2px]" style={{ background: i === steps.length - 1 ? "transparent" : ["done", "skipped"].includes(x.state) ? K.g : `${K.t3}44` }} />
            </div>
            <div className="text-[12px] font-bold mt-2" style={{ color: x.state === "ahead" ? K.t3 : K.t0 }}>{x.label}</div>
            <div className="text-[12px] mt-[2px] leading-snug" style={{ color: x.state === "rejected" ? K.r : x.state === "waiting" ? K.a : K.t2 }}>{x.who}</div>
            {x.at && <div className="text-[11px] text-t3 mt-[1px]">{when(x.at)}</div>}
          </div>
        );
      })}
    </div>
  );
};

const StatusBadge = ({ s }) => <Bg c={STATUS_COLOR[s] || K.t3} sm dot>{STATUS_LABEL[s] || s}</Bg>;

const Overlay = ({ onClose, children, wide }) => (
  <div className="fixed inset-0 animate-fadeIn bg-black/50 backdrop-blur-sm z-[250] flex items-center justify-center p-4" onMouseDown={onClose}>
    <div className={`animate-modalIn bg-bg1 border border-b0 rounded-2xl w-full ${wide ? "max-w-[720px]" : "max-w-[520px]"} max-h-[92vh] overflow-y-auto shadow-2xl`} onMouseDown={(e) => e.stopPropagation()}>
      {children}
    </div>
  </div>
);

const Label = ({ children }) => <div className="text-[12px] text-t3 mb-1 mt-3 first:mt-0">{children}</div>;

// ─── Raise a request ────────────────────────────────────────────────────
const RaiseModal = ({ opts, onClose, onDone }) => {
  const today = new Date().toISOString().slice(0, 10);
  const [f, setF] = useState({ employee_id: "", shift_id: "", date_from: today, date_to: today, reason: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const people = [{ v: "", l: "Myself" }, ...(opts.people || []).map((p) => ({ v: p.employee_id, l: `${p.name} (${p.employee_id})` }))];
  const forOther = !!f.employee_id;

  const submit = async () => {
    if (!f.shift_id) return setErr("Choose the shift you want");
    if (!f.date_from || !f.date_to) return setErr("Choose the from and to dates");
    if (f.date_to < f.date_from) return setErr("The \"to\" date cannot be before the \"from\" date");
    if (f.reason.trim().length < 3) return setErr("Please give a reason");
    setBusy(true); setErr("");
    try {
      const r = await api.post("/api/shift-requests", { ...f, employee_id: f.employee_id || undefined, shift_id: parseInt(f.shift_id) });
      notify(r.status === "Approved" ? "Shift updated" : "Request sent for approval", { title: "Done", variant: "success" });
      onDone(r);
    } catch (e) { setErr(e.message || "Could not send the request"); }
    finally { setBusy(false); }
  };

  return (
    <Overlay onClose={onClose}>
      <div className="flex items-center justify-between p-5 border-b border-b0">
        <div className="text-[16px] font-bold text-t0">Raise shift request</div>
        <button onClick={onClose} className="bg-transparent border-0 text-t3 cursor-pointer"><X size={18} /></button>
      </div>
      <div className="p-5">
        {opts.can_team && opts.people?.length > 0 && <>
          <Label>Employee</Label>
          <Sl options={people} value={f.employee_id} onChange={(v) => set("employee_id", v)} />
        </>}
        <Label>Shift you want to work</Label>
        <Sl options={[{ v: "", l: "Select a shift" }, ...(opts.shifts || []).map((s) => ({ v: s.id, l: `${s.name} (${s.start_time} – ${s.end_time})${s.is_flexible ? " · flexible" : ""}` }))]} value={f.shift_id} onChange={(v) => set("shift_id", v)} />
        <div className="grid grid-cols-2 gap-3">
          <div><Label>From</Label><Inp type="date" value={f.date_from} onChange={(v) => set("date_from", v)} /></div>
          <div><Label>To</Label><Inp type="date" value={f.date_to} onChange={(v) => set("date_to", v)} /></div>
        </div>
        <Label>Reason <span style={{ color: K.r }}>*</span></Label>
        <textarea value={f.reason} onChange={(e) => set("reason", e.target.value)} placeholder="e.g. as per roster" rows={3}
          className="w-full px-3 py-2 bg-bg2 border border-b0 rounded-[10px] text-[14px] text-t0 outline-none resize-y" />
        <div className="text-[12px] text-t3 mt-3 p-3 rounded-lg bg-bg2 border border-b0">
          {forOther
            ? "This goes to the Centre Head / Centre HR / HR Head for approval (or applies straight away if you can approve it yourself)."
            : "Your reporting manager approves first, then the Centre Head / Centre HR / HR Head give the final approval. The new shift applies only on the dates you choose."}
        </div>
        {err && <div className="mt-3 text-[13px] px-3 py-2 rounded-lg" style={{ background: K.r + "15", color: K.r }}>{err}</div>}
        <div className="flex gap-2 mt-4">
          <Bt full disabled={busy} onClick={submit}>{busy ? "Sending…" : "Submit"}</Bt>
          <Bt v="secondary" disabled={busy} onClick={onClose}>Cancel</Bt>
        </div>
      </div>
    </Overlay>
  );
};

// ─── One request: shift table + approval timeline + actions ───────────
const DetailModal = ({ id, onClose, onChanged }) => {
  const [r, setR] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [remarks, setRemarks] = useState("");
  const [rejecting, setRejecting] = useState(false);

  useEffect(() => {
    setR(null); setErr("");
    api.get(`/api/shift-requests/${id}`).then(setR).catch((e) => setErr(e.message || "Could not open this request"));
  }, [id]);

  const act = async (kind) => {
    if (kind === "reject" && remarks.trim().length < 3) return notify("Please give a reason for rejecting", { title: "Reason needed", variant: "error" });
    setBusy(true);
    try {
      const out = await api.put(`/api/shift-requests/${id}/${kind}`, { remarks });
      setR(out); setRemarks(""); setRejecting(false);
      notify(kind === "approve" ? "Approved" : kind === "reject" ? "Rejected" : "Request cancelled", { title: "Done", variant: "success" });
      onChanged();
    } catch (e) { notify(e.message || "Could not update the request", { title: "Error", variant: "error" }); }
    finally { setBusy(false); }
  };

  return (
    <Overlay onClose={onClose} wide>
      <div className="flex items-center justify-between p-5 border-b border-b0">
        <div className="flex items-center gap-3">
          <div className="text-[16px] font-bold text-t0">Shift request #{id}</div>
          {r && <StatusBadge s={r.status} />}
        </div>
        <button onClick={onClose} className="bg-transparent border-0 text-t3 cursor-pointer"><X size={18} /></button>
      </div>
      {err && <div className="p-5 text-[14px]" style={{ color: K.r }}>{err}</div>}
      {!r && !err && <div className="p-8 text-center text-t3 text-[14px]">Loading…</div>}
      {r && <div className="p-5">
        <div className="text-[12px] text-t3">Employee</div>
        <div className="text-[15px] font-semibold text-t0 mb-4">{r.employee_name} · {r.employee_id}</div>

        <div className="p-4 rounded-xl border border-b0 bg-bg2/50 mb-4"><Stepper r={r} /></div>

        <div className="rounded-xl border border-b0 overflow-hidden mb-4">
          <div className="grid grid-cols-3 bg-bg2 text-[12px] font-semibold text-t2 px-4 py-2">
            <div>Existing shift</div><div>New shift</div><div>Approval status</div>
          </div>
          <div className="grid grid-cols-3 px-4 py-3 text-[14px] text-t0 gap-3 items-start">
            <div>{shiftText(r.from_shift_name, r.from_shift_start, r.from_shift_end)}</div>
            <div>
              <div className="font-semibold">{shiftText(r.shift_name, r.shift_start, r.shift_end)}</div>
              <div className="text-[12px] text-t3 mt-[2px]">{dateRange(r.date_from, r.date_to)}</div>
            </div>
            <div><StatusBadge s={r.status} /></div>
          </div>
        </div>

        <div className="text-[12px] text-t3">Reason</div>
        <div className="text-[14px] text-t0 mb-1">{r.reason}</div>
        <div className="text-[12px] text-t3 mb-4">Raised by {r.raised_by_name || r.raised_by}{r.manager_name ? ` · Reporting manager: ${r.manager_name}` : ""}</div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-4">
          <div className="p-3 rounded-lg bg-bg2 border border-b0">
            <div className="text-[12px] text-t3">Manager approval</div>
            <div className="text-[14px] font-semibold text-t0">{r.manager_approved_by ? `${r.manager_approved_by}` : (r.timeline || []).some((e) => e.action === "Submitted") && !r.manager_name ? "No manager — not needed" : r.decided_action === "Approved" && !r.manager_approved_by ? "Approved directly" : "Pending"}</div>
            {r.manager_approved_at && <div className="text-[12px] text-t3">{when(r.manager_approved_at)}</div>}
          </div>
          <div className="p-3 rounded-lg bg-bg2 border border-b0">
            <div className="text-[12px] text-t3">Final decision</div>
            <div className="text-[14px] font-semibold text-t0">{r.decided_action ? `${r.decided_action} by ${r.decided_by || "—"}` : "Pending"}</div>
            {r.decided_at && <div className="text-[12px] text-t3">{when(r.decided_at)}</div>}
          </div>
        </div>

        <div className="text-[13px] font-bold text-t0 mb-2">Approval timeline</div>
        <div className="mb-4">
          {(r.timeline || []).map((e, i) => (
            <div key={e.id} className="flex gap-3">
              <div className="flex flex-col items-center">
                <div className="w-3 h-3 rounded-full mt-[5px]" style={{ background: EVENT_COLOR[e.action] || K.t3 }} />
                {i < r.timeline.length - 1 && <div className="w-[2px] flex-1 bg-b0 my-1" />}
              </div>
              <div className="pb-3 flex-1">
                <div className="text-[14px] font-semibold text-t0">{EVENT_LABEL[e.action] || e.action}</div>
                <div className="text-[12px] text-t3">{e.actor_name || e.actor_id} · {fmtDMY(e.created_at)} {fmtTime(e.created_at)}</div>
                {e.remarks && <div className="text-[13px] text-t1 mt-1 p-2 rounded-lg bg-bg2">{e.remarks}</div>}
              </div>
            </div>
          ))}
          {["Pending Manager", "Pending Final"].includes(r.status) && (
            <div className="flex gap-3 items-center text-[13px] text-t3"><Clock size={13} /> {r.status === "Pending Manager" ? `Waiting for ${r.manager_name || "the reporting manager"}` : "Waiting for the Centre Head / Centre HR / HR Head"}</div>
          )}
        </div>

        {(r.actions?.approve || r.actions?.cancel) && <div className="border-t border-b0 pt-4">
          {r.actions.approve && <>
            {rejecting && <textarea value={remarks} onChange={(e) => setRemarks(e.target.value)} rows={2} placeholder="Reason for rejecting (required)"
              className="w-full px-3 py-2 mb-3 bg-bg2 border border-b0 rounded-[10px] text-[14px] text-t0 outline-none resize-y" />}
            {!rejecting && <textarea value={remarks} onChange={(e) => setRemarks(e.target.value)} rows={2} placeholder="Remarks (optional)"
              className="w-full px-3 py-2 mb-3 bg-bg2 border border-b0 rounded-[10px] text-[14px] text-t0 outline-none resize-y" />}
            <div className="flex gap-2">
              {!rejecting && <Bt v="success" icon={CheckCircle} disabled={busy} onClick={() => act("approve")}>Approve</Bt>}
              {!rejecting && <Bt v="danger" icon={XCircle} disabled={busy} onClick={() => { setRejecting(true); }}>Reject</Bt>}
              {rejecting && <Bt v="danger" icon={XCircle} disabled={busy} onClick={() => act("reject")}>Confirm reject</Bt>}
              {rejecting && <Bt v="secondary" disabled={busy} onClick={() => setRejecting(false)}>Back</Bt>}
            </div>
          </>}
          {r.actions.cancel && !r.actions.approve && <Bt v="danger" disabled={busy} onClick={() => act("cancel")}>Cancel this request</Bt>}
        </div>}
      </div>}
    </Overlay>
  );
};

// ─── The tab ────────────────────────────────────────────────────────────
export const ShiftRequestsTab = ({ openId, onOpenedId }) => {
  const [opts, setOpts] = useState(null);
  const [scope, setScope] = useState("mine");
  const [rows, setRows] = useState([]);
  const [pendingN, setPendingN] = useState(0);
  const [loading, setLoading] = useState(false);
  const [raising, setRaising] = useState(false);
  const [viewId, setViewId] = useState(null);

  const load = useCallback(async (sc) => {
    setLoading(true);
    try { setRows(await api.get(`/api/shift-requests?scope=${sc}`)); } catch { setRows([]); }
    setLoading(false);
  }, []);
  const loadPendingCount = useCallback(() => {
    api.get("/api/shift-requests?scope=pending").then((d) => setPendingN((d || []).length)).catch(() => setPendingN(0));
  }, []);

  useEffect(() => { api.get("/api/shift-requests/options").then(setOpts).catch(() => setOpts({ shifts: [], people: [] })); }, []);
  useEffect(() => { load(scope); }, [scope, load]);
  useEffect(() => { if (opts?.is_approver) loadPendingCount(); }, [opts, loadPendingCount]);
  // Opened from a notification / email link: show that request.
  useEffect(() => { if (openId) { setViewId(openId); onOpenedId?.(); } }, [openId]); // eslint-disable-line

  const [mine, setMine] = useState([]);
  const loadMine = useCallback(() => { api.get("/api/shift-requests/assignments/me").then((d) => setMine(Array.isArray(d) ? d : [])).catch(() => setMine([])); }, []);
  useEffect(() => { loadMine(); }, [loadMine]);
  const refresh = () => { load(scope); loadMine(); if (opts?.is_approver) loadPendingCount(); };
  if (!opts) return <Cd><div className="p-6 text-center text-t3 text-[14px]">Loading…</div></Cd>;
  const canRaise = opts.can_self || opts.can_team;

  const SCOPES = [
    { k: "mine", l: "My requests" },
    ...(opts.is_approver ? [{ k: "pending", l: "Needs my approval", n: pendingN }, { k: "all", l: "All requests in my area" }] : []),
  ];

  const todayIso = new Date().toISOString().slice(0, 10);
  const shown = mine.filter((a) => a.date_to >= todayIso).sort((x, y) => (x.date_from < y.date_from ? -1 : 1));
  const myCard = (
    <Cd style={{ marginBottom: 12 }}>
      <div className="text-[13px] font-bold text-t0 mb-2">My shift</div>
      <div className="text-[14px] text-t0">
        Today: <span className="font-semibold">{opts.my_shift ? `${opts.my_shift.name} (${hm(opts.my_shift.start_time)} – ${hm(opts.my_shift.end_time)})` : "Default / no shift"}</span>
      </div>
      <div className="text-[12px] text-t3 mt-1 mb-2">A shift change applies only after the final approval, and only on the dates approved. Other days use your default shift.</div>
      {shown.length === 0 ? <div className="text-[13px] text-t3">No approved shift changes coming up.</div> : (
        <div className="grid gap-[6px]">
          {shown.map((a) => {
            const active = a.date_from <= todayIso && a.date_to >= todayIso;
            return (
              <div key={a.id} className="flex items-center justify-between gap-2 flex-wrap p-[10px] rounded-lg bg-bg2 border border-b0">
                <div>
                  <div className="text-[14px] font-semibold text-t0">{dateRange(a.date_from, a.date_to)} · {a.shift_name} <span className="text-t3 font-normal">({hm(a.start_time)} – {hm(a.end_time)})</span></div>
                  <div className="text-[12px] text-t3">{a.source === "request" ? "Approved shift request" : "Set by HR"}{a.reason ? ` · ${a.reason}` : ""}</div>
                </div>
                <Bg c={active ? K.g : K.bl} sm dot>{active ? "Applied now" : "Upcoming"}</Bg>
              </div>
            );
          })}
        </div>
      )}
    </Cd>
  );

  return (
    <div>
      {myCard}
      <Cd>
        <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
          <div>
            <div className="text-[16px] font-bold text-t0">Shift requests</div>
            <div className="text-[13px] text-t3">Ask to work a different shift for a range of dates. Your manager and then the Centre Head / HR approve it.</div>
          </div>
          {canRaise && <Bt icon={Plus} onClick={() => setRaising(true)}>Raise request</Bt>}
        </div>
        <div className="flex gap-[6px] mb-3 flex-wrap">
          {SCOPES.map((s) => (
            <button key={s.k} onClick={() => setScope(s.k)}
              className={`px-3 py-[6px] rounded-lg border-0 text-[13px] font-semibold cursor-pointer ${scope === s.k ? "text-p" : "bg-transparent text-t3"}`}
              style={scope === s.k ? { background: K.p + "22" } : undefined}>
              {s.l}{!!s.n && <span className="ml-[6px] inline-flex items-center justify-center min-w-[16px] h-[16px] px-1 rounded-full text-[11px] font-extrabold text-white" style={{ background: K.r }}>{s.n}</span>}
            </button>
          ))}
        </div>
        {loading ? <div className="p-6 text-center text-t3 text-[14px]">Loading…</div>
          : rows.length === 0 ? <div className="p-6 text-center text-t3 text-[14px]">{scope === "pending" ? "Nothing is waiting for your approval" : "No shift requests yet"}</div>
          : <Tbl
              onRowClick={(r) => setViewId(r.id)}
              cols={[
                { k: "id", l: "#", r: (r) => <span className="font-mono text-t2">#{r.id}</span> },
                { k: "employee", l: "Employee", r: (r) => <div><div className="font-semibold text-t0">{r.employee_name}</div><div className="text-[12px] text-t3">{r.employee_id}</div></div> },
                { k: "shift", l: "Shift change", r: (r) => <div className="flex items-center gap-[6px] flex-wrap"><span className="text-t3">{r.from_shift_name || "Default"}</span><ArrowRight size={12} className="text-t3" /><span className="font-semibold text-t0">{r.shift_name}</span></div> },
                { k: "dates", l: "Dates", r: (r) => dateRange(r.date_from, r.date_to) },
                { k: "by", l: "Raised by", r: (r) => r.raised_by_name || r.raised_by },
                { k: "status", l: "Status", r: (r) => <div className="grid gap-[6px]"><StatusBadge s={r.status} /><Stepper r={r} compact /></div> },
                { k: "log", l: "Approval log", max: 260, r: (r) => <span className="text-[12px] text-t2">{decisionText(r)}</span> },
                { k: "act", l: "", r: (r) => <Bt s="sm" v={r.actions?.approve ? "primary" : "secondary"} onClick={(e) => { e?.stopPropagation?.(); setViewId(r.id); }}>{r.actions?.approve ? "Review" : "View"}</Bt> },
              ]}
              data={rows}
            />}
      </Cd>
      {raising && <RaiseModal opts={opts} onClose={() => setRaising(false)} onDone={(r) => { setRaising(false); refresh(); setViewId(r.id); }} />}
      {viewId && <DetailModal id={viewId} onClose={() => setViewId(null)} onChanged={refresh} />}
    </div>
  );
};
